/* eslint-disable @typescript-eslint/no-unused-vars */
'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { SignInDialog } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { Cross1Icon } from '@radix-ui/react-icons';
import { FaUser } from 'react-icons/fa';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { ProductPrice } from '@eliadmin/core/src/components/product/product-price';

export type HeaderFavoritesButtonProps = {
  className?: {
    root?: string;
    info?: string;
    title?: string;
    price?: string;
    badge?: string;
  };
};

export function HeaderFavoritesButton({ className }: HeaderFavoritesButtonProps) {
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();
  const { me, isInitialized } = useCustomerStore();
  const locale = useLocaleStore(s => s.currentLocale);

  const openDialog = useDialogStore(s => s.open);

  const { isOpen, setIsOpen } = useDisclosure();
  const pathname = usePathname();
  useEffect(() => {
    setIsOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const timerRef = useRef<number | undefined>(undefined);

  const favoriteItems = useQuery({
    queryKey: [CacheKey.FAVORITE_PRODUCTS, locale, me?.authToken ?? null],
    queryFn: () =>
      // TODO: add query that fetches only count
      fetchProducts({
        locale,
        authToken: me ? me!.authToken : '',
        query: { favorite: true, results: 9999 },
      }),
    enabled: isInitialized && !!me?.authToken,
  });

  const handleMouseOver = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timerRef.current = window.setTimeout(() => {
      setIsOpen(false);
    }, 100) as unknown as number; // Type casting to number
  };

  const handleSignInClick = () => {
    openDialog({
      id: SignInDialog.id,
      content: <SignInDialog />,
    });
  };

  return (
    <div className="relative">
      <Button
        variant={HEADER_CONFIG.headerMain.favorites.buttonVariant}
        size="icon"
        className="h-12 w-12"
        asChild
        onMouseOver={handleMouseOver}
        onMouseLeave={handleMouseLeave}
      >
        <Link
          aria-label={systemLabels('favorites')}
          href={ROUTES.FAVORITES() ?? '#'}
          onClick={e => {
            if (me?.customer) return;
            e.preventDefault();
            e.stopPropagation();
            openDialog({
              id: SignInDialog.id,
              content: <SignInDialog />,
            });
          }}
          prefetch={false}
        >
          <div className="relative">
            <FavoritesBadge favoriteItems={favoriteItems.data?.data} />
            {HEADER_CONFIG.headerMain.favorites.icon}
          </div>
        </Link>
      </Button>
      {isOpen && !me && (
        <div
          onMouseOver={handleMouseOver}
          onMouseLeave={handleMouseLeave}
          className="absolute left-[-126px] top-12 z-50 min-h-40 w-72 rounded-sm border border-border bg-background p-2"
        >
          <div className="flex h-40 w-full flex-col items-center justify-center gap-2">
            <h1 className="px-1 text-center font-semibold leading-6">
              Ak chcete zobraziť obľúbené produkty, musíte sa prihlásiť.
            </h1>
          </div>
          <Button className="w-full" onClick={() => handleSignInClick()}>
            {t('text189')}
          </Button>
        </div>
      )}
      {isOpen && me && (
        <div
          onMouseOver={handleMouseOver}
          onMouseLeave={handleMouseLeave}
          className="absolute left-[-126px] top-12 z-50 min-h-40 w-72 rounded-sm border border-border bg-background p-2"
        >
          {!favoriteItems.data?.data?.length && (
            <div className="flex h-40 w-full flex-col items-center justify-center gap-2">
              <Cross1Icon className="h-16 w-16 text-destructive-foreground" />
              <h1 className="font-semibold">Žiadne obľúbené produkty</h1>
            </div>
          )}
          {!!favoriteItems.data?.data?.length && (
            <div className="flex min-h-40 flex-col justify-between">
              <div className="hide-scroll-bar flex max-h-[260px] flex-col overflow-y-scroll">
                {favoriteItems.data?.data?.map((item, index) => {
                  return (
                    <Link
                      key={`favorite-item-${item.id ?? item.slug ?? item.name ?? index}`}
                      href={item.slug}
                      className="flex items-center gap-2 rounded-sm p-2 hover:bg-[hsl(var(--primary)/0.2)]"
                    >
                      <Image
                        src={item?.image?.url || '#'}
                        alt={resolveImageAlt(item?.image, item?.name)}
                        width={64}
                        height={64}
                        className="max-h-16 max-w-16"
                      />
                      <div className="flex flex-col gap-1 font-semibold">
                        <p className="leading-2 line-clamp-2 text-sm">{item.name}</p>
                        <ProductPrice product={item} />
                      </div>
                    </Link>
                  );
                })}
              </div>
              <Button asChild className="w-full">
                <Link href={ROUTES.FAVORITES() ?? '#'} prefetch={false}>
                  {t('text61')}
                </Link>
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

type FavoritesBadgeProps = {
  favoriteItems?: Product[];
};

export function FavoritesBadge({ favoriteItems }: FavoritesBadgeProps) {
  const favoriteItemsCount = favoriteItems?.length ?? 0;

  const countText =
    favoriteItemsCount > 99 ? (
      <span>
        99<sup>+</sup>
      </span>
    ) : (
      favoriteItemsCount
    );

  return (
    <Badge
      className={cn(
        'pointer-events-none absolute right-0 top-0 aspect-square h-5 w-5 -translate-y-1/2 translate-x-1/2 justify-center bg-destructive-foreground font-light text-background',
        'ea-favorites-button__badge',
      )}
      size="circle"
    >
      {countText}
    </Badge>
  );
}
